import type { JSX, ComponentProps } from 'react'

import { useReadOnly } from '../components/read-only.tsx'
import { Button as UiButton } from '../components/ui/button.tsx'
import { cn } from '../lib/cn.ts'

const variantMap = {
  primary: 'default',
  quiet: 'ghost',
  outline: 'outline',
  danger: 'destructive'
} as const

const variantClass = {
  primary: 'hover:bg-brand-soft active:bg-brand-press',
  quiet: 'dark:hover:bg-accent',
  outline:
    'border-(length:--hairline-width) border-input bg-card dark:bg-card dark:hover:bg-accent dark:disabled:bg-sunk focus-visible:border-input',
  danger:
    'bg-destructive dark:bg-destructive text-destructive-foreground hover:bg-danger-tint dark:hover:bg-danger-tint hover:text-foreground dark:disabled:bg-sunk'
} as const

// The global outline owns focus. Override shadcn's glow and disabled opacity.
const BASE_CLASS =
  'rounded-none shadow-none focus-visible:ring-0 focus-visible:border-transparent disabled:bg-sunk disabled:text-muted-foreground disabled:opacity-100'

/**
 * A selected choice. Outline plus a brand tint, never the magenta fill —
 * magenta is the one primary action on a screen, and a selected preset is
 * not that action.
 */
const PRESSED_CLASS =
  'border-(length:--rule-width) border-rule bg-brand-tint text-foreground hover:bg-brand-tint dark:bg-brand-tint dark:hover:bg-brand-tint'

export type ButtonProps = Omit<ComponentProps<typeof UiButton>, 'variant'> & {
  'data-testid': string
  variant?: keyof typeof variantMap
  /**
   * Toggle or selected choice. Sets `aria-pressed`. When true, the button
   * draws the outline frame with a brand tint. Omit it to leave a caller's
   * own `aria-pressed` alone.
   */
  pressed?: boolean
}

/**
 * Button — the console's button.
 *
 * `data-testid` is required by the type, so the locator policy is enforced by
 * the compiler rather than by review. `type` defaults to `button`: a bare
 * button inside a form submits it, which is never what a console action means.
 *
 * Pass `asChild` with an `<a href>` inside when the action is really a
 * destination. The button then keeps its look and the reader keeps every
 * link behaviour — a new tab, a copied address, a URL on hover. `type` is
 * left off in that case, because it is not an attribute of an anchor.
 *
 * That omission has a sharp edge when the `asChild` child is itself a
 * `<button>`: nothing supplies a `type`, and HTML defaults a bare button to
 * `type="submit"`, so the child submits any form it sits inside. Such a
 * child must set its own `type`.
 *
 * @param props - Button props.
 * @param props.variant - Domain variant, mapped onto shadcn's.
 * @param props.type - Defaults to `button`, unless `asChild` is set.
 * @param props.asChild - Render the child element instead of a `<button>`.
 * @param props.className - Caller classes, which win over the house override.
 * @param props.pressed - Selected choice. Draws a tinted outline, not magenta.
 * @returns The rendered button, or nothing on a read-only screen.
 */
export function Button({
  variant = 'primary',
  type,
  asChild = false,
  className,
  pressed,
  ...props
}: ButtonProps): JSX.Element | null {
  const readOnly = useReadOnly()
  if (readOnly) return null
  const look = pressed ? 'outline' : variant
  return (
    <UiButton
      type={asChild ? type : (type ?? 'button')}
      asChild={asChild}
      variant={variantMap[look]}
      className={cn(
        BASE_CLASS,
        variantClass[look],
        pressed && PRESSED_CLASS,
        className
      )}
      {...props}
      {...(pressed === undefined ? {} : { 'aria-pressed': pressed })}
    />
  )
}
